import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Space,
  Tag,
  Tabs,
  Alert,
  Popconfirm,
  Typography,
  Table,
  Button,
  message,
  Spin,
  Modal,
} from "antd";
import {
  UserOutlined,
  TeamOutlined,
  DeleteOutlined,
  GlobalOutlined,
  PlusOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import apiClient from "../../utils/apiClient";
import SuperAdminAddAdminModal from "../../components/SuperAdminAddAdminModal";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const SuperAdminDashboard_RealData = () => {
  const navigate = useNavigate();

  const getUserFromStorage = () => {
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser && storedUser !== "undefined" && storedUser !== "null") {
        return JSON.parse(storedUser);
      }
    } catch (e) {
      console.error("Error parsing user from localStorage:", e);
    }
    return null;
  };

  const user = getUserFromStorage();

  useEffect(() => {
    if (!user || user.role !== "superadmin") {
      navigate("/login");
    }
  }, [user, navigate]);

  if (!user || user.role !== "superadmin") return null;

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalCompanies: 0,
    pendingApprovals: 0,
    companies: [],
    users: [],
  });

  const [addAdminModalVisible, setAddAdminModalVisible] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [apiError, setApiError] = useState(null);

  const getEmptyData = () => ({
    totalUsers: 0,
    activeUsers: 0,
    totalCompanies: 0,
    pendingApprovals: 0,
    companies: [],
    users: [],
  });

const fetchDashboardData = async () => {
  setLoading(true);
  setApiError(null);

  try {
    const response = await apiClient.get("/superadmin/dashboard");

    console.log("🔥 DASHBOARD RESPONSE:", response);

    const payload = response?.data;

    if (payload?.success) {
      setDashboardData(payload.data || getEmptyData());
    } else {
      setApiError(payload?.message || "API response was not successful");
      setDashboardData(getEmptyData());
    }
  } catch (error) {
    console.error(error);

    let errorMessage = "Failed to load dashboard data";

    if (error.response?.status === 401)
      errorMessage = "Authentication failed";
    else if (error.response?.status === 403)
      errorMessage = "Access denied";
    else if (error.response?.status === 500)
      errorMessage = "Server error";
    else if (!error.response)
      errorMessage = "Network error";

    setApiError(errorMessage);
    setDashboardData(getEmptyData());
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleDeleteAdmin = async (adminId, companyName) => {
    try {
      await apiClient.delete(`/superadmin/admins/${adminId}`);
      message.success(`Admin deleted from ${companyName}`);
      await fetchDashboardData();
    } catch {
      message.error("Failed to delete admin");
    }
  };

  const handleDeleteCompany = async (companyId) => {
    try {
      await apiClient.delete(`/superadmin/companies/${companyId}`);
      message.success("Company deleted successfully");
      await fetchDashboardData();
    } catch {
      message.error("Failed to delete company");
    }
  };

  const getAllAdmins = () => {
    const companies = Array.isArray(dashboardData?.companies)
      ? dashboardData.companies
      : [];

    const allAdmins = [];

    companies.forEach((company) => {
      if (Array.isArray(company.admins)) {
        company.admins.forEach((admin) => {
          allAdmins.push({
            ...admin,
            companyName: company.companyName,
            companyId: company.companyId,
          });
        });
      }
    });

    return allAdmins;
  };

  const companyColumns = [
    {
      title: "Company Name",
      dataIndex: "companyName",
      render: (text, record) => (
        <Space>
          <GlobalOutlined />
          <Text strong>{text}</Text>
          {!record.isActive && <Tag color="red">Inactive</Tag>}
        </Space>
      ),
    },
    {
      title: "Email",
      dataIndex: "companyEmail",
    },
    {
      title: "Admins",
      dataIndex: "adminCount",
      render: (count, record) => (
        <Space>
          <Tag color="blue">{count || 0} Admins</Tag>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => setSelectedCompany(record)}
          >
            View
          </Button>
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      render: (isActive) => (
        <Tag color={isActive ? "green" : "red"}>
          {isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Popconfirm
          title="Delete Company?"
          description="This action cannot be undone."
          onConfirm={() => handleDeleteCompany(record.companyId)}
          okText="Yes"
          cancelText="No"
          okType="danger"
        >
          <Button danger icon={<DeleteOutlined />}>
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const adminColumns = [
    {
      title: "Name",
      dataIndex: "name",
    },
    {
      title: "Email",
      dataIndex: "email",
    },
    {
      title: "Company",
      dataIndex: "companyName",
    },
    {
      title: "Status",
      render: (_, record) => (
        <Tag color={record.isActive ? "green" : "red"}>
          {record.isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Popconfirm
          title="Delete Admin?"
          description="This action cannot be undone."
          onConfirm={() =>
            handleDeleteAdmin(record._id || record.userId, record.companyName)
          }
          okText="Yes"
          cancelText="No"
          okType="danger"
        >
          <Button danger icon={<DeleteOutlined />}>
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ];

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 400,
        }}
      >
        <Spin size="large" />
        <Text style={{ marginLeft: 16 }}>Loading SuperAdmin Dashboard...</Text>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>SuperAdmin Dashboard</Title>

      <Space style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setAddAdminModalVisible(true)}
        >
          Add Admin
        </Button>

        <Button icon={<ReloadOutlined />} onClick={fetchDashboardData}>
          Refresh
        </Button>
      </Space>

      {apiError && <Alert type="error" message={apiError} showIcon />}

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Statistic title="Total Users" value={dashboardData.totalUsers} />
        </Col>
        <Col span={6}>
          <Statistic title="Active Users" value={dashboardData.activeUsers} />
        </Col>
        <Col span={6}>
          <Statistic
            title="Total Companies"
            value={dashboardData.totalCompanies}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="Pending Users"
            value={dashboardData.totalUsers - dashboardData.activeUsers}
          />
        </Col>
      </Row>

      <Tabs defaultActiveKey="companies">
        <TabPane tab="Companies" key="companies">
          <Table
            columns={companyColumns}
            dataSource={dashboardData.companies}
            rowKey="companyId"
            locale={{
              emptyText: apiError
                ? "Failed to load data"
                : "No companies found",
            }}
          />
        </TabPane>

        <TabPane tab="Admins" key="admins">
          <Table
            columns={adminColumns}
            dataSource={getAllAdmins()}
            rowKey={(r) => r._id || r.userId || r.email}
            locale={{
              emptyText: apiError ? "Failed to load data" : "No admins found",
            }}
          />
        </TabPane>
      </Tabs>

      <Modal
        title={`Company: ${selectedCompany?.companyName}`}
        open={!!selectedCompany}
        onCancel={() => setSelectedCompany(null)}
        footer={null}
      >
        {selectedCompany && (
          <Table
            columns={adminColumns}
            dataSource={selectedCompany.admins || []}
            rowKey={(r) => r._id || r.userId || r.email}
            pagination={false}
          />
        )}
      </Modal>

      <SuperAdminAddAdminModal
        open={addAdminModalVisible}
        onClose={() => setAddAdminModalVisible(false)}
        onSuccess={fetchDashboardData}
      />
    </div>
  );
};

export default SuperAdminDashboard_RealData;

